/**
 * Mystery Box — Plan 4b Task 8.1 (Blue + Dark) + Task 8.2 (Gold)
 *
 * Spec §4 #10: Three outcomes (1/3 each via seeded RNG):
 *   Blue  — teleport to random non-pathing tile (RN5: excludes pathing nodes)
 *   Gold  — present 3 minigame choices (sets pendingMysteryBoxChoice)
 *   Dark  — trapped immediately, skipping the rest of the current turn
 *
 * Self-registers via addItem(mysteryBox) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { mulberry32 } from '../dice';
import { addItem, allItems } from './registry';
import { modifyKeys } from '../economy';
import { isMinigameEnabled } from '@shared/config/features';
import { clearLastMove } from '../movement-options';
import { grantPlayerItem } from '../inventory';

/** Placeholder minigame pool (Plans 25-32 will replace with real IDs). */
export const MYSTERY_BOX_MINIGAME_POOL: readonly string[] = [
  'mg_a',
  'mg_b',
  'mg_c',
  'mg_d',
  'mg_e',
  'mg_f',
  'mg_g',
  'mg_h',
];

export const mysteryBox: Item = {
  id: 'mystery_box',
  name: 'Mystery Box',
  description:
    'Open a mystery box for a random outcome: Blue (teleport), Gold (minigame choice), or Dark (trapped 1 round).',
  targetType: 'self',
  category: 'complex',
  rarity: 'uncommon',
  blockedByStoneSkin: false,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId, graph } = ctx;

    // Step 1: Validate caster
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Mystery Box: caster not found: ${casterId}`);

    // Step 2: Roll outcome (1 RNG advance)
    const r1 = mulberry32(state.rngState);
    state.rngState = r1.nextState;
    const outcome = r1.value < 1 / 3 ? 'blue' : r1.value < 2 / 3 ? 'gold' : 'dark';

    // Step 3: Emit item_cast + mystery_box_outcome
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'mystery_box',
    });
    appendEvent(state, {
      type: 'mystery_box_outcome',
      playerId: casterId,
      casterId,
      itemId: 'mystery_box',
      outcome,
    });

    // Step 4: Branch by outcome
    if (outcome === 'blue') {
      // Require graph for Blue (teleport)
      if (!graph) throw new Error('Mystery Box: graph required for Blue outcome');

      const fromNodeId = caster.nodeId;
      // Pick uniform random non-pathing tile (RN5), excluding caster's current tile (P4b-C8)
      const allTiles = state.boardNodeIds.filter((id) => {
        const node = graph.getNode(id);
        return node?.type !== 'pathing' && id !== caster.nodeId;
      });

      // Guard: empty pool (P4b-C1) — emit no-op teleport, do not corrupt caster.nodeId
      if (allTiles.length === 0) {
        appendEvent(state, {
          type: 'mystery_box_teleport',
          playerId: casterId,
          casterId,
          fromNodeId,
          toNodeId: fromNodeId,
        });
        return { success: true, endsTurn: false };
      }

      const r2 = mulberry32(state.rngState);
      state.rngState = r2.nextState;
      const idx = Math.floor(r2.value * allTiles.length);
      const toNodeId = allTiles[idx];

      caster.nodeId = toNodeId;
      clearLastMove(state, casterId);
      appendEvent(state, {
        type: 'mystery_box_teleport',
        playerId: casterId,
        casterId,
        fromNodeId,
        toNodeId,
      });
      return {
        success: true,
        endsTurn: false,
        forcedRelocations: [
          {
            playerId: casterId,
            actorId: casterId,
            fromNodeId,
            toNodeId,
            reason: 'teleport',
            source: 'mystery_box_blue',
          },
        ],
      };
    } else if (outcome === 'gold') {
      if (isMinigameEnabled('mystery-box-choice')) {
        // Flag-on path: queue minigame choice
        if (state.pendingMysteryBoxChoice !== null) {
          return { success: false };
        }

        // Pick 3 distinct minigame ids from pool via Fisher-Yates partial shuffle
        const pool = [...MYSTERY_BOX_MINIGAME_POOL];
        const picks: string[] = [];
        for (let i = 0; i < 3; i++) {
          const r = mulberry32(state.rngState);
          state.rngState = r.nextState;
          const remaining = pool.length - i;
          const swapIdx = i + Math.floor(r.value * remaining);
          // Swap pool[i] and pool[swapIdx]
          const tmp = pool[i];
          pool[i] = pool[swapIdx];
          pool[swapIdx] = tmp;
          picks.push(pool[i]);
        }

        const minigameOptions: [string, string, string] = [picks[0], picks[1], picks[2]];
        state.pendingMysteryBoxChoice = { playerId: casterId, minigameOptions };
        appendEvent(state, {
          type: 'mystery_box_choice_required',
          playerId: casterId,
          casterId,
          minigameOptions,
        });
      } else {
        // Flag-off fallback: rare item from rare-pool + 50 keys
        const rarePool = allItems().filter((item) => item.rarity === 'rare');
        if (rarePool.length > 0) {
          const r = mulberry32(state.rngState);
          state.rngState = r.nextState;
          const idx = Math.floor(r.value * rarePool.length);
          const grantedItem = rarePool[idx];
          grantPlayerItem(state, casterId, grantedItem.id, 'mystery_box');
          appendEvent(state, {
            type: 'mystery_box_outcome',
            playerId: casterId,
            casterId,
            itemId: 'mystery_box',
            outcome: 'gold_fallback_item',
          });
        }
        modifyKeys(state, casterId, 50, 'mystery_box_gold_fallback');
      }
    } else {
      // Dark — trap the caster immediately and skip the rest of this turn.
      appendEvent(state, {
        type: 'mystery_box_trapped',
        playerId: casterId,
        casterId,
        expiresOnRound: state.round,
      });
      return { success: true, endsTurn: true };
    }

    return { success: true, endsTurn: false };
  },
};

// Self-register at module load
addItem(mysteryBox);
